<template>
  <div class="chart-container">
    <Gauge
      width="100%"
      height="100%"
      v-bind:valueFontStyle="
        colorTheme == 'light'
          ? 'font-size: 35px; fill: #202F38; font-weight: bold; font-family: Noto Sans'
          : 'font-size: 35px; fill: #CAC9CB; font-weight: bold; font-family: Noto Sans'
      "
      v-bind:labelFontStyle="
        colorTheme == 'light'
          ? 'font: 16px Noto Sans; fill: #7F7976'
          : 'font: 16px Noto Sans; fill: #8D9599'
      "
      v-bind:minMaxFontStyle="
        colorTheme == 'light'
          ? 'font: 16px Noto Sans; fill: #7F7976'
          : 'font: 16px Noto Sans; fill: #8D9599'
      "
      v-bind:backgroundColor="colorTheme == 'light' ? '#F6F5F6' : '#555555'"
      :shadowOpacity="0"
      :chartdata="chartdata"
      :options="options"
      :value="chartdata.value"
      :formatFunction="formatFunction"
      :minTextFormatFn="formatFunction"
      :maxTextFormatFn="formatFunction"
      :min="options.minValue"
      :max="options.maxValue"
      :labelText="options.units"
      :key="componentKey"
    />
  </div>
</template>

<script>
import { WidgetOptionItemType } from "@datadog/ui-extensions-sdk";
import Gauge from "@/components/Gauge.vue";
import { debounce, min as _min, max as _max } from "underscore";
import Pluralize from "pluralize";

export default {
  components: {
    Gauge,
  },
  data() {
    return {
      chartdata: {
        value: null,
      },
      options: {
        minValue: 0,
        maxValue: 100,
        units: null,
        activeMetric: null,
        activeHost: null,
        activeOperation: null,
        reduceOperation: null,
        countOrRate: null,
      },
      context: {
        dashboard: null,
        app: {
          currentUser: null,
          debug: null,
        },
        widget: null,
      },
      metadata: {
        description: null,
        short_name: null,
        integration: null,
        statsd_interval: null,
        per_unit: null,
        type: null,
        unit: null,
      },
      query: null,
      componentKey: 0,
      metricsList: [],
      colorTheme: "light",
      timer: null,
    };
  },
  created: function () {
    let vm = this;
    let client = this.$client;
    // Initialize the widget menu (this could also be done in the manifest)
    vm.updateWidgetOptions(client);
    client.getContext().then((c) => {
      if (c.app.debug) console.log("context: " + JSON.stringify(c));
      if (c.dashboard !== undefined) {
        vm.context = c;
        vm.colorTheme = vm.context.app.currentUser.colorTheme;
        document.body.style.backgroundColor =
          this.getColorTheme() == "light" ? "#FFFFFF" : "#222126";
        let start = c.dashboard?.timeframe?.start / 1000;
        let end = c.dashboard?.timeframe?.end / 1000;
        if (c.app.debug) console.log("Querying API from: " + start.toFixed());
        // Comment this if API limits
        // TODO cache results to avoid hitting API limits
        if (vm.metricsList.length == 0) {
          if (c.app.debug) console.log("REFRESHING METRICS LIST");
          client.api
            .get("/api/v1/metrics", {
              params: { from: start.toFixed() },
            })
            .then((d) => {
              // vm.metricsList = d["metrics"];
              if (d["metrics"].length > vm.metricsList.length) {
                vm.metricsList = d["metrics"];
              } else {
                vm.metricsList = ["system.cpu.user", "aws.route53.dnsqueries"];
              }
              vm.updateWidgetOptions(client, vm.metricsList);
            })
            .catch((e) => {
              console.error(e);
            });
        }

        if (c.widget.definition.options !== undefined) {
          let widgetOptions = c.widget.definition.options;
          let activeMetric = widgetOptions["active-metrics"] ?? null;
          let activeHost = widgetOptions["active-host"] ?? null;
          let activeOperation = widgetOptions["active-operation"] ?? null;
          let reduceOperation = widgetOptions["reduce-operation"] ?? null;
          if (vm.metadata.type == "count" || vm.metadata.type == "rate") {
            vm.alternateWidgetOptions(client, vm.metricsList);
          }
          // else {
          //   console.log("Not a count");
          //   vm.updateWidgetOptions(client, vm.metricsList);
          // }

          let minValue = parseInt(widgetOptions["min-value"]);
          let maxValue = parseInt(widgetOptions["max-value"]);

          vm.options.minValue = minValue;
          vm.options.maxValue = maxValue;
          vm.options.activeMetric = activeMetric;
          vm.options.activeHost = activeHost;
          vm.options.activeOperation = activeOperation;
          vm.options.reduceOperation = reduceOperation;

          if (activeMetric && activeHost && activeOperation) {
            createQuery(activeMetric, activeHost, activeOperation);
            client.api
              .get(`/api/v1/metrics/${activeMetric}`)
              .then((d) => {
                let metadata = d;
                vm.metadata = metadata;
                createUnits(metadata);
                clearInterval(vm.timer);
                if (vm.context.dashboard.timeframe.isLive) {
                  let temp_end = end;
                  let temp_start = start;
                  vm.timer = setInterval(() => {
                    temp_end += 30;
                    temp_start += 30;
                    vm.updateChartData(client, temp_start, temp_end, vm.query);
                  }, 30000);
                } else {
                  vm.updateChartData(client, start, end, vm.query);
                }
              })
              .catch((e) => {
                console.error(e);
              });
          }
        }
      }
    });

    client.events.on("dashboard_timeframe_change", (change) => {
      if (vm.context.app.debug)
        console.log("timeframe event received:" + JSON.stringify(change));
      vm.updateContextTimeframe(change);
      let start = change["start"] / 1000;
      let end = change["end"] / 1000;
      createUnits(vm.metadata);
      clearInterval(vm.timer);
      if (change.isLive) {
        let temp_end = end;
        let temp_start = start;
        vm.timer = setInterval(() => {
          temp_end += 30;
          temp_start += 30;
          vm.updateChartData(client, temp_start, temp_end, vm.query);
        }, 30000);
      } else {
        vm.updateChartData(client, start, end, vm.query);
      }
    });

    function createQuery(metric, host, operation) {
      if (metric && host && operation) {
        let query = `${operation}:${metric}{${host}}`;
        if (vm.context.app.debug) console.log("Query: " + query);
        vm.query = query;
      }
    }
    function createUnits(metadata) {
      if (vm.context.app.debug)
        console.log("Metadata: " + JSON.stringify(metadata));
      vm.metadata = metadata;
      let perUnit = metadata["per_unit"] ?? "";
      let unit = metadata["unit"] ?? "";
      let units;
      let value = vm.chartdata.value;
      units = unit == "percent" ? unit : Pluralize(unit, value);
      let countOrRate = vm.context.widget.definition.options["count-or-rate"];
      if (countOrRate == "rate") {
        if (!units.includes("/sec")) {
          units = units + "/sec";
        }
      }
      vm.options.units = perUnit ? `${units}/${perUnit}` : units;
    }
    // ! DON'T PUT API CALLS HERE, EACH LETTER IS ONE EVENT
    client.events.on(
      "dashboard_custom_widget_options_change",
      debounce((change) => {
        if (vm.context.app.debug)
          console.log(
            "widget options event received:" + JSON.stringify(change)
          );
        vm.updateContextWidget(change);
        let activeMetric = change["active-metrics"] ?? null;
        let activeHost = change["active-host"] ?? null;
        let activeOperation = change["active-operation"] ?? null;
        if (change.metadata?.type) {
          console.log(JSON.stringify(change.metadata.type));
          vm.options.countOrRate = change.metadata.type;
          vm.metadata.type = change.metadata.type;
        }
        if (vm.metadata.type == "count" || vm.metadata.type == "rate") {
          vm.alternateWidgetOptions(client, vm.metricsList);
        } else {
          vm.options.countOrRate = null;
          vm.updateWidgetOptions(client, vm.metricsList);
        }

        let minValue = parseInt(change["min-value"] ?? 0);
        let maxValue = parseInt(change["max-value"] ?? 100);

        if (
          vm.options.minValue != minValue ||
          vm.options.maxValue != maxValue
        ) {
          vm.options.minValue = minValue;
          vm.options.maxValue = maxValue;
          vm.forceRerender();
        }

        if (activeMetric && activeHost && activeOperation) {
          createQuery(activeMetric, activeHost, activeOperation);
          client
            .getContext()
            .then((c) => {
              if (c.dashboard !== undefined) {
                vm.context = c;
                vm.updateChartData(
                  client,
                  c.dashboard?.timeframe?.start / 1000,
                  c.dashboard?.timeframe?.end / 1000,
                  vm.query
                );
              }
            })
            .catch((e) => {
              console.error(e);
            });
          client.api
            .get(`/api/v1/metrics/${activeMetric}`)
            .then((d) => {
              let metadata = d;
              if (vm.metadata.unit != "(No data)") {
                vm.metadata = metadata;
                createUnits(metadata);
              }
            })
            .catch((e) => {
              console.error(e);
            });
        }
      }, 1000)
    );

    client.events.on("dashboard_template_var_change", (change) => {
      if (vm.context.app.debug)
        console.log("templatevar event received:" + JSON.stringify(change));
      // vm.updateContextTemplateVars(change);
    });
    // client.events.on("dashboard_cursor_change", (change) => {
    //   console.log("event received:" + JSON.stringify(change));
    // });
  },
  methods: {
    getColorTheme() {
      return this.colorTheme;
    },
    updateContextTimeframe(change) {
      this.timeframe = { start: change.start, end: change.end };
      this.$set(this.context.dashboard, "timeframe", change);
    },
    updateContextWidget(change) {
      if (this.context.app.debug)
        console.log("Change received: " + JSON.stringify(change));
      this.$set(this.context.widget.definition, "options", change);
    },
    updateContextTemplateVars(change) {
      this.$set(this.context.dashboard, "templateVars", change);
    },
    abbreviateNumber(number) {
      var SI_SYMBOL = ["", "k", "M", "G", "T", "P", "E"];
      var tier = (Math.log10(Math.abs(number)) / 3) | 0;
      if (tier == 0) return parseFloat(number.toFixed(2));
      var suffix = SI_SYMBOL[tier];
      var scale = Math.pow(10, tier * 3);
      var scaled = number / scale;
      return parseFloat(scaled.toFixed(2)) + suffix;
    },
    formatFunction(value) {
      return this.abbreviateNumber(value);
    },
    forceRerender() {
      this.componentKey += 1;
    },
    updateWidgetOptions(client, metricsList) {
      client.dashboard.customWidget.updateOptions([
        {
          type: WidgetOptionItemType.STRING,
          name: "active-metrics",
          label: "Metric",
          enum: metricsList ? (metricsList.length ? metricsList : "") : "",
          default: this.options.activeMetric ?? "system.cpu.user",
          loading: metricsList ? false : true,
          required: true,
          order: 1,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "active-host",
          label: "From",
          default: this.options.activeHost ?? "*",
          required: true,
          order: 2,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "active-operation",
          label: "Operation",
          enum: ["avg", "max", "min", "sum"],
          default: this.options.activeOperation ?? "avg",
          required: true,
          order: 3,
        },
        // {
        //   type: WidgetOptionItemType.STRING,
        //   name: "count-or-rate",
        //   label: "As",
        //   default: "disabled", // to prevent user from changing
        //   loading: true,
        //   required: true,
        //   order: 4,
        // },
        {
          type: WidgetOptionItemType.STRING,
          name: "reduce-operation",
          label: "Reduce by",
          enum: ["avg", "last", "max", "min", "sum"],
          default: this.options.reduceOperation ?? "avg",
          required: true,
          order: 5,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "min-value",
          label: "Minimum Value",
          default: 0,
          required: false,
          order: 6,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "max-value",
          label: "Maximum Value",
          default: 100,
          required: false,
          order: 7,
        },
      ]);
    },
    alternateWidgetOptions(client, metricsList) {
      client.dashboard.customWidget.updateOptions([
        {
          type: WidgetOptionItemType.STRING,
          name: "active-metrics",
          label: "Metric",
          enum: metricsList.length > 0 ? metricsList : "",
          default: this.options.activeMetric ?? "system.cpu.user",
          loading: metricsList ? false : true,
          required: true,
          order: 1,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "active-host",
          label: "From",
          default: this.options.activeHost ?? "*",
          required: true,
          order: 2,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "active-operation",
          label: "Operation",
          enum: ["avg", "max", "min", "sum"],
          default: this.options.activeOperation ?? "avg",
          required: true,
          order: 3,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "count-or-rate",
          label: "As",
          enum: ["count", "rate"],
          default: this.options.countOrRate ?? "count",
          required: false,
          order: 4,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "reduce-operation",
          label: "Reduce by",
          enum: ["avg", "last", "max", "min", "sum"],
          default: this.options.reduceOperation ?? "avg",
          required: true,
          order: 5,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "min-value",
          label: "Minimum Value",
          default: 0,
          required: false,
          order: 6,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "max-value",
          label: "Maximum Value",
          default: 100,
          required: false,
          order: 7,
        },
      ]);
    },
    updateChartData(client, start, end, query) {
      if (this.context.app.debug)
        console.log("Metric Type: " + this.metadata.type);
      let countOrRate = this.context.widget.definition.options["count-or-rate"];
      if (this.metadata.type == "count" || this.metadata.type == "rate") {
        this.alternateWidgetOptions(client, this.metricsList);
        let asFunction = `.as_${countOrRate}()`;
        if (this.context.app.debug) {
          console.log("Count type detected");
          console.log("new query: " + this.query + asFunction);
        }
        if (query.includes(asFunction)) {
          if (this.context.app.debug) console.log("No change needed");
        } else {
          if (this.context.app.debug) console.log("Appending count to query");
          query = query + asFunction;
        }
      } else {
        this.options.countOrRate = "disabled";
        this.updateWidgetOptions(client, this.metricsList);
      }
      if (this.context.app.debug) console.log("Updating with query: " + query);
      client.api
        .get("/api/v1/query", {
          params: { from: start, to: end, query: query },
        })
        .then((d) => {
          let series = d.series[0];
          // get reduce operation
          let reduce =
            this.context.widget.definition.options["reduce-operation"];
          let pointlist = series && series.pointlist;
          let valueList = pointlist && pointlist.map((x) => x[1]);
          if (!valueList) {
            this.chartdata.value = 0;
            this.options.units = "(No data)";
            return;
          }

          let value;
          switch (reduce) {
            case "avg": {
              value =
                valueList.reduce(function (sum, val) {
                  return sum + val;
                }, 0) / valueList.length;
              break;
            }
            case "last": {
              value = valueList[valueList.length - 1];
              break;
            }
            case "max": {
              value = _max(valueList);
              break;
            }
            case "min": {
              value = _min(valueList);
              break;
            }
            case "sum": {
              value = valueList.reduce((a, b) => a + b, 0);
              break;
            }
            default: {
              break;
            }
          }
          if (this.context.app.debug) console.log(`Value: ${value}`);
          // TODO Handle very large numbers over 1k
          // Handle bytes
          if (this.options.units == "bytes/minute") {
            if (this.context.app.debug) console.log("Converting to Bytes");
            if (value >= 1073741824) {
              value = value / 1.073741824;
            } else if (value >= 1048576) {
              value = value / 1.048576;
            } else if (value >= 1024) {
              value = value / 1.024;
            }
            if (this.context.app.debug) console.log("New value: " + value);
          }
          this.chartdata.value = value;
        })
        .catch((e) => {
          console.error(e);
        });
    },
  },
};
</script>
<style>
@import url("https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;700&display=swap");
.chart-container {
  overflow: hidden;
  position: relative;
  padding: 0px;
  height: 88vh;
  width: 97vw;
}
</style>